import styled from '@emotion/styled'
import {
    ApiContentSection,
    getInspectSectionAttrs,
} from '@storefronts/common-types'
import React, { useContext } from 'react'
import { EditorSectionHighlight } from '../../shared/EditorSectionHighlight'
import {
    SECTION_CONTAINER_NAME,
    ScrollingLayout,
    inScrollingLayout,
    pageBleed,
    sectionGutter,
} from '../../../style/scrollingLayout'
import { Constants } from '../../../style/styleConstants'
import {
    isDesktopReadySection,
    sectionBleedsToPage,
} from './desktopReadySections'

/**
 * A background band widens the element the content sits in, and `width: 100%`
 * would then resolve against the page rather than the section — putting banded
 * content on a different edge from its unbanded neighbours. `100cqw` is the
 * section container's own width, which is what the content would have had if
 * nothing had bled.
 */
const ignoreBleed = {
    width: '100cqw',
    marginInline: 'auto',
}

const PaddedWrapper = styled.div<{ insideBleed?: boolean }>(
    ({ insideBleed }) => ({
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        paddingLeft: Constants.CONTENT_BLOCK_MOBILE_MARGIN,
        paddingRight: Constants.CONTENT_BLOCK_MOBILE_MARGIN,
        minWidth: 0,
        ...inScrollingLayout({ paddingInline: sectionGutter }),
        ...(insideBleed ? ignoreBleed : null),
    }),
)

export const InlineWrapper = styled.div<{ insideBleed?: boolean }>(
    ({ insideBleed }) => ({
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        minWidth: 0,
        ...(insideBleed ? ignoreBleed : null),
    }),
)

/**
 * A section's background band. It runs the width of the page while the content
 * inside it stays where it is, so a colour change reads as a band across the
 * page rather than a block floating in a column.
 */
const SectionBackground = styled.div(pageBleed)

/**
 * Declares the container a section's own rules are written against, and caps
 * the section until it has been migrated to use them.
 *
 * This has to be a separate element from the section's content: a container
 * query never matches the element that declares the container, only its
 * descendants.
 *
 * `bleed` applies pageBleed on this element (not a descendant) so a
 * desktop-ready HTML section's container — the box `@container section`
 * queries — is the page width, and authored backgrounds can reach the
 * edges. The fragment holds copy and grids to the 1200px column.
 */
const SectionContainer = styled.div<{ maxWidth?: number; bleed?: boolean }>(
    ({ maxWidth, bleed }) => ({
        containerType: 'inline-size',
        containerName: SECTION_CONTAINER_NAME,
        width: '100%',
        ...(maxWidth === undefined
            ? null
            : { maxWidth, marginInline: 'auto' as const }),
        ...(bleed ? pageBleed : null),
    }),
)

/**
 * Editor-selected index for the enclosing section tree, or undefined when this
 * tree is not the one being edited. Every tree provides its own value, so a
 * tree nested inside another (an INLINE_PDP that includes PDP body content)
 * never inherits an index that belongs to its parent.
 */
export const SelectedSectionIdxContext = React.createContext<
    number | undefined
>(undefined)

/**
 * Shared section shell: chooses padded vs inline layout and stamps inspect
 * attrs on the outermost element (full-bleed background div when present).
 */
export const Section: React.FC<{
    section: ApiContentSection & { id?: string }
    index: number
    inline?: boolean
    backgroundColor?: string
    sectionRef?: (el: HTMLDivElement | null) => void
    stampInspectAttrs: boolean
    children: React.ReactNode
}> = ({
    section,
    index,
    inline = false,
    backgroundColor,
    sectionRef,
    stampInspectAttrs,
    children,
}) => {
    const inspectAttrs = stampInspectAttrs
        ? getInspectSectionAttrs(section, index)
        : {}
    const Wrapper = inline ? InlineWrapper : PaddedWrapper
    const selectedIdx = useContext(SelectedSectionIdxContext)

    const isSelected = selectedIdx === index
    const highlightAnchor = isSelected
        ? ({ position: 'relative' } as const)
        : undefined

    const content = backgroundColor ? (
        <SectionBackground
            style={{ backgroundColor, ...highlightAnchor }}
            {...inspectAttrs}
        >
            <Wrapper ref={sectionRef} insideBleed>
                {children}
            </Wrapper>
            {isSelected && <EditorSectionHighlight />}
        </SectionBackground>
    ) : (
        <Wrapper ref={sectionRef} {...inspectAttrs} style={highlightAnchor}>
            {children}
            {isSelected && <EditorSectionHighlight />}
        </Wrapper>
    )

    // The container wraps the section rather than replacing anything, so the
    // inspect attrs, the ref and the highlight anchor all stay on one element
    // and the editor sees a single box per section.
    return (
        <SectionContainer
            maxWidth={
                isDesktopReadySection(section)
                    ? undefined
                    : ScrollingLayout.UNMIGRATED_SECTION_MAX_WIDTH
            }
            bleed={sectionBleedsToPage(section)}
        >
            {content}
        </SectionContainer>
    )
}
